import React from 'react';
import { motion } from 'framer-motion';
import { 
  Upload, 
  Share2, 
  Bot, 
  CheckCircle, 
  ArrowRight,
  Zap,
  Clock
} from 'lucide-react';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';

const ProcessStepsSimplified = () => {
  const { t } = usePublicLanguage();

  const steps = [
    {
      icon: Upload,
      title: t('home.process.simplified.upload.title'),
      description: t('home.process.simplified.upload.description'),
      avatarClass: 'process-avatar-blue',
      iconClass: 'text-blue-700'
    },
    {
      icon: Share2,
      title: t('home.process.simplified.share.title'),
      description: t('home.process.simplified.share.description'),
      avatarClass: 'process-avatar-green',
      iconClass: 'text-green-700'
    },
    {
      icon: Bot,
      title: t('home.process.simplified.analyze.title'),
      description: t('home.process.simplified.analyze.description'),
      avatarClass: 'process-avatar-violet',
      iconClass: 'text-violet-700'
    },
    {
      icon: CheckCircle,
      title: t('home.process.simplified.select.title'),
      description: t('home.process.simplified.select.description'),
      avatarClass: 'process-avatar-orange',
      iconClass: 'text-orange-700'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50 process-handwritten-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center process-arrow-sticker mb-6">
            <Zap className="h-4 w-4 mr-2" />
            {t('home.process.simplified.badge')}
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 process-hand-title">
            {t('home.process.simplified.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto process-hand-subtitle">
            {t('home.process.simplified.subtitle')}
          </p>
        </motion.div>

        {/* Time Comparison */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-md mx-auto mb-16"
        >
          <div className="process-paper-card">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <div className="process-marker-icon process-marker-red">
                  <Clock className="h-6 w-6 text-red-600" />
                </div>
                <div className="text-sm text-gray-700 mb-1 process-hand-label">{t('home.process.simplified.traditional')}</div>
                <div className="text-2xl font-bold text-red-600 process-hand-value">{t('home.process.simplified.traditionalTime')}</div>
              </div>
              <div className="process-sketch-arrow" aria-hidden="true">→</div>
              <div className="text-center flex-1">
                <div className="process-marker-icon process-marker-green">
                  <Zap className="h-6 w-6 text-green-600" />
                </div>
                <div className="text-sm text-gray-700 mb-1 process-hand-label">{t('home.process.simplified.withCandio')}</div>
                <div className="text-2xl font-bold text-green-600 process-hand-value">{t('home.process.simplified.candioTime')}</div>
              </div>
            </div>
            <div className="mt-4 text-center process-savings-line">
              <span className="text-sm text-slate-700 process-hand-label">{t('home.process.simplified.savings')}</span>
              <span className="text-sm font-bold text-primary-700 ml-1 process-hand-value">90%</span>
            </div>
          </div>
        </motion.div>

        {/* Simplified Steps Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => {
            const Icon = step.icon;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`group relative flex flex-col ${
                  index < steps.length - 1 ? 'pb-6 lg:pb-0' : ''
                }`}
              >
                <article className="process-candidate-card relative z-10">
                  <div className="process-candidate-body">
                    <div className="process-candidate-profile">
                      <div className={`process-candidate-avatar ${step.avatarClass}`}>
                        <Icon className={`h-8 w-8 process-hand-drawn-icon ${step.iconClass}`} />
                      </div>
                      <div className="process-candidate-content">
                        <h3 className="process-candidate-title">
                          {step.title}
                        </h3>
                        <p className="process-candidate-description">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    <div className="process-candidate-meta-lines" aria-hidden="true">
                      <span className="process-candidate-line w-[88%]" />
                      <span className="process-candidate-line w-[72%]" />
                      <span className="process-candidate-line w-[84%]" />
                    </div>
                  </div>

                </article>

                {index < steps.length - 1 && (
                  <div
                    className="hidden lg:block process-workflow-arrow-desktop absolute top-1/2 -right-12 -translate-y-1/2"
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 108 42" className="w-24 h-9">
                      <path
                        d="M6 31 C30 8, 66 7, 94 22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 29 C27 7, 64 8, 91 24"
                        fill="none"
                        stroke="currentColor"
                        strokeOpacity="0.45"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M85 13 L101 22 L84 30"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}

                {index < steps.length - 1 && (
                  <div
                    className="lg:hidden flex justify-center mt-4 process-workflow-arrow-mobile relative z-30 pointer-events-none"
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 32 56" className="w-8 h-12">
                      <path
                        d="M16 5 C14 20, 14 32, 16 42"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10 38 L16 49 L22 38"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-gray-600 mb-6">
            {t('home.process.simplified.cta')}
          </p>
          <a
            href="/signup"
            className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-lg md:text-xl font-semibold rounded-xl hover:from-primary-600 hover:to-primary-700 transform hover:scale-105 transition-all shadow-xl"
          >
            {t('home.process.simplified.ctaButton')}
            <ArrowRight className="ml-3 h-6 w-6" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessStepsSimplified;
